import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Dropdown, Modal, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useRecoilState } from "recoil";
import { brandKitsState } from "../../../../../state/atoms";
import { BrandKitDto } from "../../../../../generated/dashboard-client";
import BrandTemplateApi, { BrandTemplateDto } from "../../../../../api/BrandTemplateApi";
import "./CopyToBrandKitModal.scss";

interface CopyToBrandKitModalProps {
  open: boolean;
  templates: BrandTemplateDto[];
  currentBrandKitId?: string;
  onClose: () => void;
  onCopied?: (kitNames: string[]) => void;
}

const kitMeta = (kit: BrandKitDto) => {
  const locations = kit.locationIds?.length ?? 0;
  const templates = kit.templateCount ?? 0;
  return `${locations} location${locations === 1 ? "" : "s"} • ${templates} template${templates === 1 ? "" : "s"}`;
};

export default function CopyToBrandKitModal({
  open,
  templates,
  currentBrandKitId,
  onClose,
  onCopied,
}: CopyToBrandKitModalProps) {
  const [brandKits] = useRecoilState(brandKitsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>();

  useEffect(() => {
    if (open) {
      setSelected([]);
      setDropdownOpen(false);
    }
  }, [open]);

  const currentKit = useMemo(
    () => brandKits.find((b) => b.id === currentBrandKitId),
    [brandKits, currentBrandKitId]
  );

  const otherKits = useMemo(
    () => brandKits.filter((b) => b.id && b.id !== currentBrandKitId),
    [brandKits, currentBrandKitId]
  );

  const allSelected = otherKits.length > 0 && selected.length === otherKits.length;

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleAll() {
    setSelected(allSelected ? [] : otherKits.map((b) => b.id!));
  }

  const selectedNames = otherKits
    .filter((b) => selected.includes(b.id!))
    .map((b) => b.name || "Untitled");

  const triggerText = allSelected
    ? "All Brand Kits"
    : selectedNames.length
    ? selectedNames.join(", ")
    : "Select";

  const copyableCount = templates.filter((t) => t.id).length;

  const confirmText =
    selected.length === 1
      ? `Copy to ${selectedNames[0]}`
      : selected.length > 1
      ? `Copy to ${selected.length} Brand Kits`
      : "Copy";

  function close() {
    if (copying) return;
    onClose();
  }

  function handleCopy() {
    const copyableIds = templates.filter((t) => t.id).map((t) => t.id!);
    if (selected.length === 0 || copyableIds.length === 0) return;
    setCopying(true);

    const targets = otherKits.filter((b) => selected.includes(b.id!));
    const targetIds = targets.map((b) => b.id!);

    BrandTemplateApi.copyBulk(copyableIds, targetIds)
      .then((resp) => {
        if (!resp.ok) throw new Error();
        const names = targets.map((b) => b.name || "Untitled");
        const tplNoun = `template${copyableIds.length === 1 ? "" : "s"}`;
        const kitNoun = `Brand Kit${names.length === 1 ? "" : "s"}`;
        message.success(`${copyableIds.length} ${tplNoun} copied to ${names.length} ${kitNoun}`);
        onCopied?.(names);
        onClose();
      })
      .catch(() => message.error("Failed to copy to Brand Kit"))
      .finally(() => setCopying(false));
  }

  const dropdownPanel = (
    <div className="copy-to-brand-kit-panel" style={{ width: dropdownWidth }}>
      {currentKit && (
        <div className="copy-to-brand-kit-option current" key="current">
          <span className="copy-to-brand-kit-option-label">
            <Checkbox checked disabled />
            <span className="copy-to-brand-kit-option-text">
              <span className="copy-to-brand-kit-option-name">
                {currentKit.name || "Untitled"}
              </span>
              <span className="copy-to-brand-kit-option-meta">{kitMeta(currentKit)}</span>
            </span>
          </span>
          <span className="copy-to-brand-kit-current-tag">current Brand Kit</span>
        </div>
      )}

      {otherKits.length > 1 && (
        <div className="copy-to-brand-kit-option" key="all" onClick={toggleAll}>
          <span className="copy-to-brand-kit-option-label">
            <Checkbox checked={allSelected} />
            <span className="copy-to-brand-kit-option-text">
              <span className="copy-to-brand-kit-option-name">All Brand Kits</span>
            </span>
          </span>
        </div>
      )}

      {otherKits.map((b) => (
        <div className="copy-to-brand-kit-option" key={b.id} onClick={() => toggle(b.id!)}>
          <span className="copy-to-brand-kit-option-label">
            <Checkbox checked={selected.includes(b.id!)} />
            <span className="copy-to-brand-kit-option-text">
              <span className="copy-to-brand-kit-option-name">{b.name || "Untitled"}</span>
              <span className="copy-to-brand-kit-option-meta">{kitMeta(b)}</span>
            </span>
          </span>
        </div>
      ))}

      {otherKits.length === 0 && (
        <div className="copy-to-brand-kit-empty">No other Brand Kits</div>
      )}
    </div>
  );

  return (
    <Modal
      className="copy-to-brand-kit-modal"
      open={open}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnClose
    >
      <header>
        <h2>Copy to other Brand Kit</h2>
        <p>Copies are independent — changes won't affect the originals.</p>
        {copyableCount > 1 && (
          <span className="copy-to-brand-kit-count">{copyableCount} templates selected</span>
        )}
      </header>

      <Dropdown
        overlay={dropdownPanel}
        trigger={["click"]}
        visible={dropdownOpen}
        onVisibleChange={(v) => {
          if (v && triggerRef.current) {
            setDropdownWidth(triggerRef.current.offsetWidth);
          }
          setDropdownOpen(v);
        }}
        overlayClassName="copy-to-brand-kit-dropdown"
        placement="bottomLeft"
      >
        <div
          ref={triggerRef}
          className={`copy-to-brand-kit-trigger${dropdownOpen ? " open" : ""}`}
        >
          <span
            className={`copy-to-brand-kit-trigger-text${
              selectedNames.length ? "" : " placeholder"
            }`}
          >
            {triggerText}
          </span>
          <DownOutlined />
        </div>
      </Dropdown>

      <footer>
        <Button size="large" onClick={close} disabled={copying}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={handleCopy}
          loading={copying}
          disabled={selected.length === 0}
        >
          {confirmText}
        </Button>
      </footer>
    </Modal>
  );
}
